import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

/**
 * Hook to persist filter state across navigation using localStorage + URL params
 *
 * Priority:
 * 1. URL params (if present) - for shared links and browser back/forward
 * 2. localStorage (if URL empty) - for SPA navigation
 * 3. Default values
 *
 * @param storageKey - Unique key for localStorage
 * @param defaultValues - Default filter values
 * @param urlParams - Current URL search params
 * @returns Object with updateFilters, clearFilters, and isInitialized
 */
export function usePersistedFilters<T extends Record<string, unknown>>(
  storageKey: string,
  defaultValues: T,
  urlParams: T
) {
  const navigate = useNavigate();
  const hasInitialized = useRef(false);
  const navigateRef = useRef(navigate);
  const [isInitialized, setIsInitialized] = useState(false);

  // Keep navigate ref up to date
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Initialize filters on mount - runs only once
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // Check if URL has any filter params (excluding defaults) at this moment
    const urlHasFilters = Object.keys(urlParams).some(key => {
      const urlValue = urlParams[key];
      const defaultValue = defaultValues[key];
      return urlValue !== undefined && urlValue !== defaultValue;
    });

    // Priority 1: If URL has filters, save them to localStorage
    if (urlHasFilters) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(urlParams));
      } catch (error) {
        console.error('Failed to save filters to localStorage:', error);
      }
      setIsInitialized(true);
      return;
    }

    // Priority 2: If URL is empty, try to restore from localStorage
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsedFilters = JSON.parse(stored);
        // Only restore if there are actual filter values
        const hasStoredFilters = Object.keys(parsedFilters).some(key => {
          const value = parsedFilters[key];
          const defaultValue = defaultValues[key];
          return value !== undefined && value !== defaultValue;
        });

        if (hasStoredFilters) {
          // Restore filters by updating URL
          navigateRef.current({
            search: { ...defaultValues, ...parsedFilters },
            replace: true, // Use replace to avoid adding to history
          });
          // Mark as initialized after a brief delay to allow navigate to process
          setTimeout(() => setIsInitialized(true), 0);
          return;
        }
      }
    } catch (error) {
      console.error('Failed to restore filters from localStorage:', error);
    }

    // No restoration needed, mark as initialized
    setIsInitialized(true);
  }, [storageKey]); // Only depend on storageKey - run once per mount

  /**
   * Update filters in both URL and localStorage
   */
  const updateFilters = (updates: Record<string, unknown>) => {
    const newFilters = { ...urlParams, ...updates };

    // Update URL (cast to any - TanStack Router's search type is complex and doesn't accept generic records)
    navigateRef.current({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: newFilters as any,
      resetScroll: false,
    });

    // Save to localStorage
    try {
      localStorage.setItem(storageKey, JSON.stringify(newFilters));
    } catch (error) {
      console.error('Failed to save filters to localStorage:', error);
    }
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    // Update URL to defaults (cast to any - TanStack Router's search type is complex and doesn't accept generic records)
    navigateRef.current({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: defaultValues as any,
      resetScroll: false,
    });

    // Clear from localStorage
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Failed to clear filters from localStorage:', error);
    }
  };

  return {
    updateFilters,
    clearFilters,
    isInitialized,
  };
}
